import { canvas, ctx2d } from '../render/pixel';
import { iconPixels } from './compose';
import { handMade, type IconKind, type IconSize, iconKinds, kindDef } from './store';

/**
 * The icon registry: every file under `icons/<kind>/` is loaded here, each describing a kind
 * (`kind.ts`, with its generator) or adding hand-made pictures (`registerIcon`). Ask for an icon
 * with `icon(kind, id, size)`; it is drawn once per size and kept.
 *
 * Show icons with `image-rendering: pixelated` at whole-number scales (32 → 32/64/96 px,
 * 16 → 16/32/48 px), through `iconEl` so each place gets its own element.
 */
import.meta.glob('./*/*.ts', { eager: true });

export { registerIcon, registerIcons, defineIconKind, handMade, iconKinds, kindDef } from './store';
export type { IconArt, IconCtx, IconKind, IconSize, Marker, Plate } from './store';
export { iconPixels } from './compose';

const cache = new Map<string, HTMLCanvasElement>();

/** The icon for an id of a kind, drawn once per size (the same canvas every time after). */
export function icon(kind: IconKind, id: string, size: IconSize = 32): HTMLCanvasElement {
  const key = `${kind}:${id}:${size}`;
  const hit = cache.get(key);
  if (hit) return hit;
  const px = iconPixels(kind, id, size);
  const c = canvas(size, size);
  const g = ctx2d(c);
  const img = g.createImageData(size, size);
  new Uint32Array(img.data.buffer).set(px.d);
  g.putImageData(img, 0, 0);
  cache.set(key, c);
  return c;
}

/** Every id of a kind, and whether its picture is hand-made. */
export function iconIndex(kind: IconKind): { id: string; name: string; hand: boolean }[] {
  const def = kindDef(kind);
  if (!def) return [];
  return def.ids().map((id) => ({ id, name: def.name(id), hand: handMade(kind, id) }));
}

/** Sizes an icon is drawn at. */
export const ICON_SIZES: readonly IconSize[] = [32, 16];

export const allIconKinds = (): IconKind[] => iconKinds();
